import * as React from 'react';

import { cn } from '@/lib/utils';

import { variantElementMap } from './Typography.constants';
import { typographyVariants } from './Typography.styles';

import type { TypographyProps } from './Typography.types';

/**
 * Typography Component
 *
 * Renders text with consistent styling based on semantic variants.
 * Automatically maps variants to appropriate HTML elements while allowing override via 'as' prop.
 *
 * @example
 * ```tsx
 * <Typography variant="h1">Page Title</Typography>
 * <Typography variant="body" textColor="secondary">Description text</Typography>
 * <Typography variant="caption" truncate>Long text that will truncate</Typography>
 * ```
 */
const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    { className, variant = 'body', weight, align, textColor, truncate, as, children, ...props },
    ref
  ) => {
    const Element =
      as ||
      (variant && variant in variantElementMap ? variantElementMap[variant] : undefined) ||
      'p';

    return (
      <Element
        ref={ref}
        className={cn(
          typographyVariants({ variant, weight, align, color: textColor, truncate }),
          className
        )}
        {...props}
      >
        {children}
      </Element>
    );
  }
);

Typography.displayName = 'Typography';

export { Typography };
